import { useState } from 'react'
import { Button } from './Button'
import { Modal } from './Modal'

export type CopyTarget = { id: string; label: string; /** e.g. "Replaces its Regular hours". */ note?: string }

/**
 * Pick other locations to copy something to. Copies are independent: editing one location later never
 * changes the others, so a copy is a starting point when the details differ.
 */
export function CopyToDialog({ open, onClose, what, targets, onCopy }: { open: boolean; onClose: () => void; what: string; targets: CopyTarget[]; onCopy: (ids: string[]) => void }) {
  const [picked, setPicked] = useState<string[]>([])
  // Start fresh next time it opens.
  const close = () => {
    setPicked([])
    onClose()
  }
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))
  return (
    <Modal
      open={open}
      onClose={close}
      title={`Copy ${what} to…`}
      footer={
        <>
          <Button variant="ghost" size="sm" arrow={false} onClick={close}>
            Cancel
          </Button>
          <Button size="sm" arrow={false} disabled={picked.length === 0} onClick={() => {
              onCopy(picked)
              setPicked([])
            }}>
            {picked.length > 1 ? `Copy to ${picked.length} locations` : 'Copy'}
          </Button>
        </>
      }
    >
      {targets.length === 0 ? (
        <p className="text-small text-dim">There are no other locations to copy to.</p>
      ) : (
        <div className="grid gap-4">
          <p className="text-small text-dim">Each location gets its own copy, so you can still change the details at one without touching the others.</p>
          <ul className="grid divide-y divide-gray-200 border-y border-gray-200">
            {targets.map((t) => (
              <li key={t.id}>
                <label className="flex cursor-pointer items-start gap-3 py-3">
                  <input type="checkbox" checked={picked.includes(t.id)} onChange={() => toggle(t.id)} className="mt-1 h-4 w-4 shrink-0 accent-[var(--color-ink)]" />
                  <span className="grid gap-0.5">
                    <span className="text-ink">{t.label}</span>
                    {t.note && <span className="text-small text-dim">{t.note}</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  )
}
